// store/layers.ts
import { proxy } from 'valtio';
import { debugSetter, parseFontString, urlToFile } from '../../shared/utils/common.tsx';
import { fileStore } from '../../store/fileStore.ts';
import { getVideoEl, setVideoEl, deleteVideoEl } from '../../store/videoStore.ts';
import { Effect } from './layers/effect.model.tsx';
import { LayerDataType } from './types/layerType.ts';
import { TextLayerData } from './layers/textLayerData.model.tsx';
import { VideoLayerData } from './layers/videoLayerData.model.tsx';
import { ImageLayerData } from './layers/imageLayerData.model.tsx';
import { ColorLayerData } from './layers/colorLayerData.model.tsx';
import { AudioLayerData } from './layers/audioLayerData.model.tsx';
import { EffectStep } from './layers/effectStep.model.ts';
import type Konva from "konva"
import { validate } from 'uuid';
import { LayerData } from './layers/layerData.model.tsx';
import { getValueAtTime, parseLayerDataJSON } from './common/helpers.tsx';
import FontFaceObserver from "fontfaceobserver";
import { LayerSerializer } from './serializers/layerSerializer.ts';
import { Tags } from '../../shared/models/tags.model.ts';
import { TagGroup } from '../../shared/models/tagGroup.model.ts';
import { DropShadowEffect } from './layers/effectsType.model.tsx';
import { Comment } from '../../shared/models/comment.model.ts';

export const layerStore = proxy({
  //
  refreshView: false as boolean,
  adMode: false as boolean,
  selectedAdId: "" as string,

  // Template Level
  selectedAdObjectId: "" as string,
  selectedAdObjectName: "" as string,
  mediaPreview: "" as string,

  layersInFlight: new Set<string>(),

  // Layer Object
  layer: null as (null | LayerDataType),

  //static
  staticMode: false as boolean,

  // Forms
  currentTime: 0,
  renderInt: 0,
  activeForm: "layers" as string,
  propertyPanelIsOpen: false as boolean,
  timelinePanelIsOpen: false as boolean,
  selectedLayerIds: [] as string[],
  selectedEffectIds: [] as string[],
  selectedStepIds: [] as string[],
  buffering: false,

  // Konva
  konvaLayerRefs: {} as Record<string, any>,
  konvaShapeRefs: {} as Record<string, any>,
  konvaStageRef: null as any,
  konvaMainLayerRef: null as any,
  konvaTransformerRef: null as any,

  // Overlay
  loadingLayer: false as boolean,
  showToast: false as boolean,
  toastMessage: { message: "Empty Toast" } as ToastMessage,

  // Tags
  tagGroups: [] as TagGroup[],
  tagsLinkedToTemplate: [] as TagGroup[],

  // Template Settings
  copy_text: "",
  link: "",
  link_headline: "",
  link_description: "",
  cta_text: "",
  is_default: false,

  // Full ad content for PATCH updates
  adContent: null as any,

  // Comments
  comments: [] as Comment[]

});

interface ToastMessage {
  message: string;
  level?: "log" | "success" | "warning" | "error";
}
export const setComments = (comments: Comment[]) => {
  layerStore.comments = comments
}

export const setTagGroups = (tagGroups: TagGroup[]) => {
  layerStore.tagGroups = tagGroups
}

export const addTag = (tag: Tags, tagGroupId: number) => {
  layerStore.tagGroups.forEach((tagGroup) => {
    if (tagGroup.id == tagGroupId) {
      tagGroup.tags.push(tag);
    }
  })
}

export const removeTag = (tag: Tags, tagGroupId: number) => {
  layerStore.tagGroups.forEach((tagGroup) => {
    if (tagGroup.id == tagGroupId) {
      tagGroup.tags = tagGroup.tags.filter(t => t.id !== tag.id);
    }
  })
}

export const setTagsLinkedToTemplate = (tagsLinkedToTemplate: TagGroup[]) => {
  layerStore.tagsLinkedToTemplate = tagsLinkedToTemplate
}
export const setTagToLinkedTemplate = (tag: Tags, tagGroupId: number) => {
  layerStore.tagsLinkedToTemplate.forEach((tagGroup) => {
    if (tagGroup.id == tagGroupId) {
      tagGroup.tags.push(tag);
    }
  })
}

export const addTagToLinkedTemplate = (tag: Tags, tagGroupId: number) => {
  // Find existing tag group
  const existingTagGroupLinked = layerStore.tagsLinkedToTemplate.find(
    tagGroup => tagGroup.id === tagGroupId
  );

  if (existingTagGroupLinked) {
    existingTagGroupLinked.tags.push(tag);
  } else {
    // Create new tag group and add tag
    const newTagGroup = new TagGroup();
    const tagGroup = layerStore.tagGroups.find(group => group.id === tagGroupId);
    if (tagGroup) {
      newTagGroup.name = tagGroup.name
      newTagGroup.id = tagGroup.id
      newTagGroup.color = tagGroup.color
    }
    newTagGroup.tags = [tag] // Initialize tags array with the new tag
    layerStore.tagsLinkedToTemplate.push(newTagGroup);
  }
}

export const showMessage = debugSetter("showMessage", (message: string, level?: "log" | "success" | "warning" | "error", duration?: number) => {
  layerStore.showToast = true;
  layerStore.toastMessage = { message: message, level: level };
  if (!duration) return;
  setTimeout(() => {
    layerStore.showToast = false
  }, duration);
})

export const setSelectedAdId = debugSetter("setSelectedAdId", (adId: string) => {
  layerStore.selectedAdId = adId;
})

const getNextLayerOrder = (children: LayerData[]): number => {
  return children.reduce((maxOrder, child) => {
    const childOrder = Number.isFinite(child.order) ? child.order : -1;
    return Math.max(maxOrder, childOrder);
  }, -1) + 1;
};

export const addLayerFromUrl = debugSetter("addLayerFromUrl", (type: string, url: string) => {
  if (layerStore.adMode) return;
  const mainLayer = layerStore.layer;
  if (mainLayer === null || mainLayer.type !== "composition") return;

  const nextOrder = getNextLayerOrder(mainLayer.children);
  let newLayer: LayerDataType;
  switch (type) {
    case "video":
      newLayer = new VideoLayerData({ source: url, order: nextOrder, end_time: mainLayer.end_time });
      break;
    case "image":
      newLayer = new ImageLayerData({ source: url, order: nextOrder, end_time: mainLayer.end_time });
      break;
    case "audio":
      newLayer = new AudioLayerData({ source: url, order: nextOrder, end_time: mainLayer.end_time });
      break;
    default:
      console.error("addLayerFromUrl: unsupported type", type);
      return;
  }

  newLayer.dirty = true;
  mainLayer.children = [...mainLayer.children, newLayer];
  layerStore.selectedLayerIds = [newLayer.id];
  layerStore.activeForm = 'layer';
  return newLayer.id;
});

export const addLayer = debugSetter("addLayer", (type: string, fileId?: string) => {
  if (layerStore.adMode) return;
  const mainLayer = layerStore.layer;
  if (mainLayer === null) {
    console.error("Cannot add layer without a layer in layerStore.");
    return;
  }

  if (mainLayer.type !== "composition") {
    console.error("Cannot add layer to non-composition layer."); // How did we even get here...
    return;
  }

  const nextOrder = getNextLayerOrder(mainLayer.children);
  let newLayer: LayerDataType;
  switch (type) {
    case "text":
      newLayer = new TextLayerData({
        order: nextOrder,
        end_time: mainLayer.end_time
      })
      break
    case "color":
      newLayer = new ColorLayerData({
        order: nextOrder,
        end_time: mainLayer.end_time
      })
      break
    case "image": {
        if (!fileId) return
        const file = fileStore.getFile(fileId);
        if (!file) return
        const url = URL.createObjectURL(file)
        newLayer = new ImageLayerData({
          source: url,
          order: nextOrder,
          end_time: mainLayer.end_time
        })
        break
    }
    case "video": {
        if (!fileId) return
        const videoFile = fileStore.getFile(fileId);
        if (!videoFile) return
        const videoUrl = URL.createObjectURL(videoFile)
        newLayer = new VideoLayerData({
          source: videoUrl,
          order: nextOrder,
          end_time: mainLayer.end_time
        })
        break
    }

    case "audio": {
        if (!fileId) return
        const audioFile = fileStore.getFile(fileId);
        if (!audioFile) return
        const audioUrl = URL.createObjectURL(audioFile)
        newLayer = new AudioLayerData({
          source: audioUrl,
          order: nextOrder,
          end_time: mainLayer.end_time
        })
        break
    }
    default:
      console.error("Called addLayer with an unknown type");
      return;
  }

  newLayer.dirty = true;
  mainLayer.children = [...mainLayer.children, newLayer]
  layerStore.selectedLayerIds = [newLayer.id];
  layerStore.activeForm = 'layer';
  return newLayer.id
});

export const deleteLayer = debugSetter('deleteLayer', async (layerId: string) => {
  if (layerStore.adMode) return;
  const mainLayer = layerStore.layer;
  if (mainLayer === null) {
    console.error("Cannot delete layer without a layer in layerStore.");
    return;
  }

  if (mainLayer.type !== "composition") {
    console.error("Cannot delete layer to non-composition layer."); // How did we even get here...
    return;
  }

  const found = findLayerById(layerId);
  if (!found) {
    console.error(`Couldn't find layer ${layerId} to delete`)
    return
  }
  const { layerIndex } = found

  const deletedLayer = mainLayer.children.splice(layerIndex, 1)[0];
  for (let i = layerIndex; i < mainLayer.children.length; i++) {
    mainLayer.children[i].order = mainLayer.children[i].order - 1;
  }
  if (!validate(deletedLayer.id)) {
    const { CreateServiceV3 } = await import("./CreateService");
    const service = new CreateServiceV3()
    service.deleteLayer(deletedLayer.id)
  }

})

export const duplicateLayer = debugSetter("duplicateLayer", (layerId: string) => {
  if (layerStore.adMode) return
  const mainLayer = layerStore.layer;
  if (mainLayer === null) {
    console.error("Cannot duplicate layer without a layer in layerStore.");
    return;
  }

  if (mainLayer.type !== "composition") {
    console.error("Cannot duplicate layer to non-composition layer."); // How did we even get here...
    return;
  }
  const found = findLayerById(layerId);
  if (!found) {
    console.error(`Couldn't find layer ${layerId} to duplicate`)
    return
  }
  const { layer } = found
  const data = JSON.parse(JSON.stringify(layer));
  data.id = crypto.randomUUID();
  data.order = getNextLayerOrder(mainLayer.children);
  if (data.name) {
    data.name = data.name + " Copy";
  }
  const newLayer = new LayerSerializer().deserialize(data);
  newLayer.dirty = true;
  mainLayer.children = [...mainLayer.children, newLayer]
});

export const swapLayers = debugSetter("swapLayer", (from: string, to: string) => {
  if (layerStore.adMode) return
  const mainLayer = layerStore.layer;
  if (mainLayer === null) {
    console.error("Cannot swap layers without a layer in layerStore.");
    return;
  }

  if (mainLayer.type !== "composition") {
    console.error("Cannot swap layers in a non-composition layer."); // How did we even get here...
    return;
  }

  const foundFrom = findLayerById(from);
  const foundTo = findLayerById(to);
  if (!foundFrom) {
    console.error(`Couldn't find layer ${from} to delete`)
    return
  }
  if (!foundTo) {
    console.error(`Couldn't find layer ${to} to delete`)
    return
  }

  const { layerIndex: fromLayerIndex } = foundFrom
  const { layerIndex: toLayerIndex } = foundTo


  if (fromLayerIndex < 0 || fromLayerIndex > mainLayer.children.length - 1) return
  if (toLayerIndex < 0 || toLayerIndex > mainLayer.children.length - 1) return

  const layersCopy = [...mainLayer.children]

  const tempTo = layersCopy[toLayerIndex]
  layersCopy[toLayerIndex] = layersCopy[fromLayerIndex]
  layersCopy[fromLayerIndex] = tempTo

  const swappedOrder = layersCopy[toLayerIndex].order
  layersCopy[toLayerIndex].order = layersCopy[fromLayerIndex].order
  layersCopy[fromLayerIndex].order = swappedOrder

  layersCopy[toLayerIndex].dirty = true;
  layersCopy[fromLayerIndex].dirty = true;
  mainLayer.children = layersCopy
})

interface FoundLayer {
  parent: LayerData | null  // `null` means it was the root composition itself
  layer: LayerData
  layerIndex: number
}

export function findLayerById(targetId: string): FoundLayer | null {
  const main = layerStore.layer
  if (main === null) return null
  if (main.id === targetId) {
    return { parent: null, layer: main, layerIndex: 0 }
  }
  if (main.type !== "composition") {
    // if it’s not a composition, then it cannot have children
    return null
  }

  // find in children
  const idx = main.children.findIndex((child) => child.id === targetId)
  if (idx !== -1) {
    return { parent: main, layer: main.children[idx], layerIndex: idx }
  }

  return null
}


interface FoundEffect {
  layer: LayerData
  effectIndex: number
  effect: Effect
}


export function findEffectById(
  layerId: string,
  effectId: string
): FoundEffect | null {
  const found = findLayerById(layerId)
  if (!found) return null

  const { layer } = found
  // We assume every LayerData has an `.effects: Effect[]` array.
  const eIndex = layer.effects.findIndex((e) => e.id === effectId)
  if (eIndex === -1) {
    return null
  }
  return {
    layer,
    effectIndex: eIndex,
    effect: layer.effects[eIndex],
  }
}

export function findEffectByType(
  layerId: string,
  effectType: string
): FoundEffect | null {
  const found = findLayerById(layerId)
  if (!found) return null

  const { layer } = found
  const eIndex = layer.effects.findIndex((e) => e.type === effectType)
  if (eIndex === -1) {
    return null
  }
  return {
    layer,
    effectIndex: eIndex,
    effect: layer.effects[eIndex],
  }
}

interface FoundStep {
  layer: LayerData
  effect: Effect
  step: EffectStep
  stepIndex: number;
}

export function findStepById(layerId: string, effectId: string, stepId: string): FoundStep | null {
  const found = findEffectById(layerId, effectId);
  if (!found) return null;

  const { layer, effect } = found;
  const sIndex = effect.steps.findIndex((s) => s.id === stepId);
  if (sIndex === -1) {
    return null;
  }
  return {
    layer: layer,
    effect: effect,
    step: effect.steps[sIndex],
    stepIndex: sIndex
  }
}

export const addEffect = debugSetter("addEffect", (layerId: string, type: string) => {
  const found = findLayerById(layerId);
  if (!found) return null;

  const { layer } = found;
  let newEffect;

  switch (type) {
    case "dropshadow":
      newEffect = new DropShadowEffect({})
      break
  }

  if (newEffect) {
    layer.dirty = true;
    layer.effects = [...layer.effects, newEffect]
  }
})

export const updateEffectParams = debugSetter("updateEffect", (layerId: string, effectId: string, field: string, value: any) => {
  const found = findEffectById(layerId, effectId);
  if (!found) return null;

  const { effect, layer } = found;
  layer.dirty = true;
  effect.params[field] = value;
})

export const deleteEffect = debugSetter("deleteEffect", (layerId: string, effectId: string) => {
  const found = findEffectById(layerId, effectId);
  if (!found) return null;

  const { layer } = found;
  layer.dirty = true;
  layer.effects = layer.effects.filter((effect) => effect.id !== effectId)
})

export const updateStep = debugSetter("updateStep", (layerId: string, effectId: string, stepId: string, field: string, value: any) => {
  const found = findStepById(layerId, effectId, stepId);
  if (!found) return

  const { effect, stepIndex, layer } = found;
  const newSteps = [...effect.steps]
  newSteps[stepIndex] = { ...newSteps[stepIndex], [field]: value }

  layer.dirty = true;
  effect.steps = newSteps;
})

export const updateLayer = debugSetter(
  "updateLayer",
  (layerId: string, field: string, value: any) => {
    if (layerStore.layer === null) return
    if (layerStore.layer.type !== "composition") return

    const found = findLayerById(layerId)
    if (!found) {
      console.error(`Error when updating layer. Cannot find layer with id ${layerId}`)
      return
    }

    const { parent, layer } = found

    if (parent === null) {
      layerStore.layer = parseLayerDataJSON({ ...layer, [field]: value })
    } else {
      const newChild = parseLayerDataJSON({ ...layer, [field]: value })
      const idx = parent.children.findIndex((c) => c.id === layer.id)
      if (idx === -1) {
        console.error(
          `Impossible: we found a layer by ID but then lost it in the children array.`
        )
        return
      }
      newChild.dirty = true;
      parent.children.splice(idx, 1, newChild)
    }
  }
)

export const setKeyframeEnabled = debugSetter("setKeyframeEnabled", (layerId: string, effectId: string, enabled: boolean) => {
  if (layerStore.layer === null) return
  if (layerStore.layer.type !== "composition") return
  const found = findEffectById(layerId, effectId)
  if (!found) {
    console.error(
      `Error when getting layer effect. Cannot find effect with id ${effectId} in layer ${layerId}`
    )
    return
  }
  const { effect, layer } = found;
  layer.dirty = true;
  const current_value = getValueAtTime(effect, layerStore.currentTime);
  const current_time = enabled ? layerStore.currentTime : 0

  effect.keyframeEnabled = enabled;
  effect.steps = [new EffectStep({
    time: current_time,
    value: current_value
  })]
})


export const addEffectValueAtTime = debugSetter(
  "addEffectValue",
  (layerId: string, effectId: string, time: number, value: any) => {
    if (layerStore.layer === null) return
    if (layerStore.layer.type !== "composition") return

    const found = findEffectById(layerId, effectId)
    if (!found) {
      console.error(
        `Error when getting layer effect. Cannot find effect with id ${effectId} in layer ${layerId}`
      )
      return
    }

    const { layer, effectIndex, effect } = found
    layer.dirty = true;
    const newStep = new EffectStep({ time, value })
    let newSteps: EffectStep[]
    if (!effect.keyframeEnabled) {
      if (layerStore.adMode) {
        if (effect.steps.length > 1) console.warn("Keyframe disabled and too many steps...");
        newStep.id = effect.steps[0].id
        newStep.time = effect.steps[0].time
      } else {
        newStep.time = 0
      }

      newSteps = [newStep]
      // In this mode, we simply throw out all other keyframes and keep only [newStep]
      layer.effects[effectIndex].steps = newSteps
      return
    }

    // Find insertion position (first step whose time >= new time)
    const nextIndex = effect.steps.findIndex((step) => step.time >= time)

    if (nextIndex > -1 && effect.steps[nextIndex].time === time) {
      // Overwrite the existing step at that exact time
      newSteps = [...effect.steps]
      newSteps[nextIndex].value = value
    } else if (nextIndex === -1) {
      // New time is greater than all existing keys → push to end
      if (layerStore.adMode) {
        showMessage("Cannot add keyframes in ad mode", "warning", 5000)
        setRefreshView(true);
        return;
      }
      newSteps = [...effect.steps, newStep]
    } else if (nextIndex === 0) {
      // We’re strictly before the very first keyframe → unshift
      if (layerStore.adMode) {
        showMessage("Cannot add keyframes in ad mode", "warning", 5000)
        setRefreshView(true);

        return;
      }
      newSteps = [newStep, ...effect.steps]
    } else {
      // We’re inserting somewhere in the middle
      if (layerStore.adMode) {
        showMessage("Cannot add keyframes in ad mode", "warning", 5000)
        setRefreshView(true);

        return;
      }
      newSteps = [...effect.steps]
      newSteps.splice(nextIndex, 0, newStep)
    }

    // Finally, replace steps array (mutating in place)
    layer.effects[effectIndex].steps = newSteps
  }
)

export const deleteValueAtTime = debugSetter("deleteValueAtTime", (layerId, effectId, time: number) => {
  if (layerStore.adMode) {
    showMessage("Cannot delete keyframes in ad mode", "warning", 3000)
    return;
  }
  const foundEffect = findEffectById(layerId, effectId);
  if (!foundEffect) {
    console.error(`Couldn't find effect with id ${effectId} to delete`)
    return
  }

  const { effect, layer } = foundEffect
  layer.dirty = true;

  if (effect.steps.length == 1) {
    const current_value = getValueAtTime(effect, layerStore.currentTime);
    effect.steps = [new EffectStep({
      time: 0,
      value: current_value
    })]
    return
  }

  const stepIndex = effect.steps.findIndex((step: EffectStep) => {
    return step.time === time;
  });

  if (stepIndex > -1) {
    const newSteps = [...effect.steps]
    newSteps.splice(stepIndex, 1)
    effect.steps = newSteps;
  }
})


export const onDragEnd = debugSetter(
  "onDragEnd",
  (layerId: string, e: Konva.KonvaEventObject<DragEvent>) => {
    // Find the “position” effect and add a keyframe at the current time
    const foundEffect = findEffectByType(layerId, "position")
    if (!foundEffect) return
    const { effect, layer } = foundEffect
    layer.dirty = true;
    addEffectValueAtTime(
      layerId,
      effect.id,
      layerStore.currentTime,
      [e.target.x(), e.target.y()]
    )
  }
)

export const onTransformEnd = debugSetter(
  "onTransformEnd",
  (layerId: string, e: Konva.KonvaEventObject<Event>) => {
    const posFound = findEffectByType(layerId, "position")
    const scaleFound = findEffectByType(layerId, "scale")
    if (!posFound || !scaleFound) return

    const x = e.target.x()
    const y = e.target.y()
    addEffectValueAtTime(layerId, posFound.effect.id, layerStore.currentTime, [x, y])

    const found = findLayerById(layerId);
    if (!found) return
    const { layer } = found
    layer.dirty = true;

    // Konva scaleX/Y is fractional (e.g. 1.0 means 100%)
    addEffectValueAtTime(
      layerId,
      scaleFound.effect.id,
      layerStore.currentTime,
      [e.target.scaleX() * 100, e.target.scaleY() * 100]
    )
  }
)

export const saveLayer = debugSetter(
  "saveLayer",
  async (layerId: string, shouldShowToast?: boolean) => {
    if (shouldShowToast) {
      showMessage("Saving...", "log");
    }
    const layersInFlightCopy = new Set(layerStore.layersInFlight)
    if (layersInFlightCopy.has(layerId)) {
      console.info("Layer is already in flight");
      showMessage("Layer is still saving...", "warning", 3000)
      return;
    }
    const mainComposition = layerStore.layer
    if (mainComposition === null || mainComposition.type !== "composition") {
      return
    }

    const found = findLayerById(layerId)
    if (!found) {
      console.error(`Couldn't find layer with id ${layerId} to save...`)
      return
    }
    const { parent, layer } = found
    let response: any

    try {
      let file: File | null = null
      let compressedFile: File | null = null
      const source = (layer as any).source
      const compressedSource = (layer as any).compressed_source
      const isRemoteUrl = (url: string) => url.startsWith("http://") || url.startsWith("https://")

      if (source && !isRemoteUrl(source)) {
        file = await urlToFile(source, crypto.randomUUID())
      }
      if (compressedSource && !isRemoteUrl(compressedSource)) {
        compressedFile = await urlToFile(compressedSource, crypto.randomUUID())
      }
      const { CreateServiceV3 } = await import("./CreateService");

      const service = new CreateServiceV3()

      if (layer.id !== mainComposition.id && validate(mainComposition.id)) {
        await saveLayer(mainComposition.id)
        const newParentId = mainComposition.children[0].id

        response = await service.createLayer(
          newParentId,
          Number(layerStore.selectedAdObjectId),
          layer,
          { source: file ?? (source && isRemoteUrl(source) ? source : null), compressedSource: compressedFile ?? (compressedSource && isRemoteUrl(compressedSource) ? compressedSource : null) }
        )

        if (response) {
          updateLayerId(layer.id, response.id.toString());
          layer.dirty = false;
        } else {
          throw new Error("Error updating layer...");
        }
        return;
      }


      if (validate(layer.id)) {
        setLayersInFlight(layersInFlightCopy.add(layer.id))
        response = await service.createLayer(
          parent ? parent.id : null,
          Number(layerStore.selectedAdObjectId),
          layer,
          { source: file ?? (source && isRemoteUrl(source) ? source : null), compressedSource: compressedFile ?? (compressedSource && isRemoteUrl(compressedSource) ? compressedSource : null) }
        )
        if (response) {
          updateLayerId(layer.id, response.id.toString());
          const deleted = layersInFlightCopy.delete(layer.id);
          if (!deleted) {
            console.warn("Tried to delete a layer in flight that wasn't there");
          }
          setLayersInFlight(layersInFlightCopy);

          const found = findLayerById(response.id.toString());
          if (!found) return;
          const { layer: newLayer } = found;
          newLayer.dirty = false;
          if (response.layer_properties_object?.source || response.layer_properties_object?.compressed_source) {
            if (response.layer_properties_object.source) (newLayer as VideoLayerData).source = response.layer_properties_object.source;
            if (response.layer_properties_object.compressed_source) (newLayer as VideoLayerData).compressed_source = response.layer_properties_object.compressed_source;
          }
        } else {
          throw new Error("Error updating layer...");
        }

      } else {
        response = await service.updateLayer(
          parent ? parent.id : null,
          layer,
          { source: file ?? (source && isRemoteUrl(source) ? source : null), compressedSource: compressedFile ?? (compressedSource && isRemoteUrl(compressedSource) ? compressedSource : null) }
        )
        if (!response) {
          throw new Error("Error updating layer...");
        } else {
          layer.dirty = false;
          if (response.layer_properties_object?.source || response.layer_properties_object?.compressed_source) {
            if (response.layer_properties_object.source) (layer as VideoLayerData).source = response.layer_properties_object.source;
            if (response.layer_properties_object.compressed_source) (layer as VideoLayerData).compressed_source = response.layer_properties_object.compressed_source;
          }
        }
      }
      for (const child of layer.children.filter((child: LayerData) => child.dirty)) {
        await saveLayer(child.id, false);
      }

    } catch (err) {
      console.error(err)
      if (shouldShowToast) {
        showMessage("Unexpected error occured...!", "error", 3000)
      }
    } finally {
      setLoadingLayer(false)
    }

    if (shouldShowToast && response) {
      showMessage("Saved!", "success", 3000)
    }
  }
)

export const updateLayerId = debugSetter(
  "updateLayerId",
  (id: string, newId: string) => {
    if (layerStore.layer === null) return
    if (layerStore.layer.type !== "composition") return

    const indexToChange = layerStore.selectedLayerIds.findIndex((selectedId: string) => id === selectedId)
    layerStore.selectedLayerIds[indexToChange] = newId;
    setSelectedLayerIds(layerStore.selectedLayerIds);

    const found = findLayerById(id)
    if (!found) {
      console.error(`Error when updating layer. Cannot find layer with id ${id}`)
      return
    }

    const { parent, layer } = found

    // Move cached video element to new ID so reconstruction doesn't reload it
    const cachedVideo = getVideoEl(id);
    if (cachedVideo) {
      setVideoEl(newId, cachedVideo);
      deleteVideoEl(id);
    }

    if (parent === null) {
      const newChildren = layer.children.map((child) => {
        const newChild = { ...child, parent_id: newId }
        return newChild;
      })
      layerStore.layer.children = newChildren;
    }

    updateLayer(id, "id", newId);
  }
)

/**
 * Loads font CSS into the DOM and waits for the browser to make it available.
 * Returns true if the font loaded successfully, false otherwise.
 */
export async function ensureFontLoaded(fontString: string): Promise<boolean> {
  const { familyName, weight, italic } = parseFontString(fontString);
  const weightNum = parseInt(weight, 10) || 400;
  const styleId = `font-css-${familyName.replace(/\s+/g, "-")}-${weight}${italic ? "i" : ""}`;

  if (!document.getElementById(styleId)) {
    try {
      const { FontService } = await import("../../shared/services/font/FontService");
      const service = new FontService();
      const css = await service.getFontCss(familyName, weightNum, italic);
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = css;
      document.head.appendChild(style);
    } catch (err) {
      console.warn(`Failed to fetch font CSS for ${fontString}`, err);
      return false;
    }
  }

  const observer = new FontFaceObserver(familyName, {
    weight: weight,
    style: italic ? "italic" : "normal",
  });

  try {
    await observer.load(null, 10000);
    return true;
  } catch (err) {
    console.warn(`Font ${fontString} failed to load in 10s`, err);
    return false;
  }
}

export const setFont = debugSetter("setFont", async (layerId: string, fontString: string) => {
  const loaded = await ensureFontLoaded(fontString);
  if (loaded) {
    updateLayer(layerId, "font", fontString);
  }
})

export const setSelectedTemplateId = debugSetter(
  "setSelectedTemplateId",
  (id: string) => {
    layerStore.selectedAdObjectId = id
  }
)

export const setSelectedTemplateName = debugSetter(
  "setSelectedTemplateName",
  (name: string) => {
    layerStore.selectedAdObjectName = name
  }
)

export const setLayer = debugSetter(
  "setLayer",
  (layer: any) => {
    layerStore.layer = layer
  }
)

export const setCurrentTime = debugSetter(
  "setCurrentTime",
  (time: number) => {
    layerStore.currentTime = time
  }
)

export const setRenderInt = debugSetter(
  "setRenderInt",
  (int: number) => {
    layerStore.renderInt = int
  }
)

export const setActiveForm = debugSetter(
  "setActiveForm",
  (form: string) => {
    layerStore.activeForm = form
  }
)

export const setPropertyPanelIsOpen = debugSetter(
  "setPropertyPanelIsOpen",
  (isOpen: boolean) => {
    layerStore.propertyPanelIsOpen = isOpen
  }
)

export const setTimelinePanelIsOpen = debugSetter(
  "setTimelinePanelIsOpen",
  (isOpen: boolean) => {
    layerStore.timelinePanelIsOpen = isOpen
  }
)

export const setSelectedLayerIds = debugSetter(
  "setSelectedLayerIds",
  (ids: string[]) => {
    layerStore.selectedLayerIds = ids
  }
)

export const setSelectedEffectIds = debugSetter(
  "setSelectedEffectIds",
  (ids: string[]) => {
    layerStore.selectedEffectIds = ids
  }
)

export const setSelectedStepIds = debugSetter(
  "setSelectedStepIds",
  (ids: string[]) => {
    layerStore.selectedStepIds = ids
  }
)

export const setBuffering = debugSetter(
  "setBuffering",
  (isBuffering: boolean) => {
    layerStore.buffering = isBuffering
  }
)

export const setKonvaLayerRefs = debugSetter(
  "setKonvaLayerRefs",
  (refs: Record<string, LayerDataType>) => {
    layerStore.konvaLayerRefs = refs
  }
)

export const setKonvaShapeRefs = debugSetter(
  "setKonvaShapeRefs",
  (refs: Record<string, any>) => {
    layerStore.konvaShapeRefs = refs
  }
)

export const setKonvaStageRef = debugSetter(
  "setKonvaStageRef",
  (ref: any) => {
    layerStore.konvaStageRef = ref
  }
)

export const setKonvaMainLayerRef = debugSetter(
  "setKonvaMainLayerRef",
  (ref: any) => {
    layerStore.konvaMainLayerRef = ref
  }
)

export const setKonvaTransformerRef = debugSetter(
  "setKonvaTransformerRef",
  (ref: any) => {
    layerStore.konvaTransformerRef = ref
  }
)

export const setLoadingLayer = debugSetter(
  "setLoadingLayer",
  (isLoading: boolean) => {
    layerStore.loadingLayer = isLoading
  }

)

export const setRefreshView = debugSetter(
  "setRefreshView",
  (refreshView: boolean) => {
    layerStore.refreshView = refreshView;
  }
)

export const setLayersInFlight = debugSetter(
  "setLayersInFlight",
  (layersInFlight: Set<string>) => {
    layerStore.layersInFlight = layersInFlight;
  }
)
